import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, CardBody, Container, Row, Col, Button, Modal, ModalHeader } from 'reactstrap';
import Select from 'react-select';
import { getSentryProjects, postSentryServiceMapping } from '../../store/sentry/actions';
import CardItem from "components/PurpleTheme/CardItem";
import { Link } from 'react-router-dom';
import { showNotification } from "components/Common/notification";
import { getServices as onGetServices } from '../../store/service/actions';

const SentryProjects = () => {
  const dispatch = useDispatch();
  const [checkBox, setCheckBox] = useState({});
  const [mapServiceModal, setMapServiceModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedEnvironment, setSelectedEnvironment] = useState(null);
  const [currentHoverItem, setCurrentHoverItem] = useState(null);
  const [btnHover, setBtnHover] = useState(false);

  const { projects, loading, services } = useSelector(state => ({
    projects: state.Sentry.projects || [],
    loading: state.Sentry.loading,
    services: state.Service.services || []
  }));

  const environments = [
    { value: 'PRODUCTION', label: 'PRODUCTION' },
    { value: 'UAT', label: 'UAT' },
    { value: 'STAGING', label: 'STAGING' },
    { value: 'PREPROD', label: 'PREPROD' },
    { value: 'DEV', label: 'DEV' },
    { value: 'OTHER', label: 'OTHER' }
  ];

  useEffect(() => {
    dispatch(getSentryProjects());
    dispatch(onGetServices());
  }, [dispatch]);

  const serviceOptions = services.map(service => ({
    value: service.id,
    label: service.name
  }));

  const handleCheckBox = (projectId) => {
    setCheckBox(prev => ({
      ...prev,
      [projectId]: !prev[projectId]
    }));
  };

  const handleMassSelect = () => {
    const anySelected = Object.values(checkBox).some(value => value);
    const newCheckBox = {};
    projects.forEach(project => {
      newCheckBox[project.project_id] = !anySelected;
    });
    setCheckBox(newCheckBox);
  };

  const handleMapToService = () => {
    const selectedProjects = projects.filter(project => checkBox[project.project_id]);
    const existingServiceId = selectedProjects[0]?.application_service_id;
    const existingEnvironment = selectedProjects[0]?.environment;

    if (selectedProjects.every(project => project.application_service_id === existingServiceId)) {
      setSelectedService(serviceOptions.find(option => option.value === existingServiceId));
    }
    if (selectedProjects.every(project => project.environment === existingEnvironment)) {
      setSelectedEnvironment(environments.find(option => option.value.toLowerCase() === existingEnvironment.toLowerCase()));
    } else {
      setSelectedEnvironment(null);
    }
    setMapServiceModal(true);
  };

  const handleServiceMapping = () => {
    if (!selectedService || !selectedEnvironment) {
      showNotification('error', 'Please select both service and environment');
      return;
    }

    const selectedProjectIds = Object.entries(checkBox)
      .filter(([_, isSelected]) => isSelected)
      .map(([projectId]) => projectId);

    const payload = {
      project_ids: selectedProjectIds,
      application_service_id: selectedService.value,
      environment: selectedEnvironment.value
    };

    dispatch(postSentryServiceMapping(payload));
    setMapServiceModal(false);
    setSelectedService(null);
    setSelectedEnvironment(null);
    setCheckBox({});
  };

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      background: '#382b42',
      borderColor: state.isFocused ? 'white' : 'rgba(255, 255, 255, 0.7)',
      color: 'white'
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#292036' : state.isFocused ? '#2d2941' : '#382b42',
      color: 'white'
    }),
    menu: (provided) => ({
      ...provided,
      background: '#382b42'
    }),
    singleValue: (provided) => ({
      ...provided,
      color: 'white'
    }),
    input: (provided) => ({
      ...provided,
      color: 'white'
    })
  };

  const handleServiceFormChange = (selectedOption) => {
    setSelectedService(selectedOption);
  };

  const customOptionRenderer = (option) => {
    if (option.value === "/add-service") {
      return (
        <div
          className="d-flex justify-content-center align-items-center gap-2 btn btn-soft-primary"
          onMouseEnter={() => setBtnHover(true)}
          onMouseLeave={() => setBtnHover(false)}
          style={{cursor:"pointer"}}>
          <i className={`fas fa-plus font-size-14 ${btnHover ? 'text-white' : 'text-primary'}`} />
          <span>
            {option.label}
          </span>

        </div>
      );
    } else {
      return option.label;
    }
  };

  useEffect(() => {
    // Initialize the checkBox state based on the projects data
    const initialCheckBoxState = {};
    projects.forEach(project => {
      initialCheckBoxState[project.project_id] = false; // Default to unchecked
    });
    setCheckBox(initialCheckBoxState);
  }, [projects]);

  return (
    <div className="page-content">
      <Container fluid>
        <Row className="d-flex align-items-center mb-4">
          <Col lg="9">
            <h4 className="mb-0">Sentry Projects</h4>
          </Col>
          <Col lg="3" className="d-flex justify-content-end">
            <Button
              color="primary"
              disabled={!Object.values(checkBox).some(value => value)}
              onClick={handleMapToService}
            >
              Map to Service
            </Button>
          </Col>
        </Row>

        <CardItem className="mb-2">
          <div className="d-flex justify-content-evenly text-center py-2">
            <Col lg={1}>
              <i
                className={`bx ${
                  Object.values(checkBox).every(value => value) ? 'bx-check-square' : 'bx-square'
                } bx-sm`}
                onClick={handleMassSelect}
                style={{ cursor: 'pointer' }}
              />
            </Col>
            <Col lg={3}>Project Name</Col>
            <Col lg={3}>Project ID</Col>
            <Col lg={3}>Application Service</Col>
          </div>
        </CardItem>

        {loading ? (
          <div className="text-center mt-4">
            <i className="bx bx-loader bx-spin" />
          </div>
        ) : projects.length === 0 ? (
          <div className="text-center mt-4">No Projects Available</div>
        ) : (
          projects.map(project => (
            <CardItem
              key={project.project_id}
              className="card-item"
              onMouseEnter={() => setCurrentHoverItem(project.project_id)}
              onMouseLeave={() => setCurrentHoverItem(null)}
              style={{
                backgroundColor: currentHoverItem === project.project_id ? '#382d45' : ''
              }}
            >
              <div className="d-flex justify-content-evenly text-center py-3">
                <Col lg={1}>
                  <i
                    className={`bx ${
                      checkBox[project.project_id] ? 'bx-check-square' : 'bx-square'
                    } bx-sm`}
                    onClick={() => handleCheckBox(project.project_id)}
                    style={{ cursor: 'pointer' }}
                  />
                </Col>
                <Col lg={3}>{project.project_name}</Col>
                <Col lg={3}>{project.project_id}</Col>
                <Col lg={3}>
                  {project.application_service_id ? (
                    <Link to={`/temperstack-notifications/${project.application_service_id}`}>
                      {services.find(service => service.id === project.application_service_id)?.name || '-'}
                    </Link>
                  ) : (
                    '-'
                  )}
                </Col>
              </div>
            </CardItem>
          ))
        )}

        <Modal
          isOpen={mapServiceModal}
          centered
          size="xl"
          onClosed={() => setMapServiceModal(false)}
        >
        <div className="modal-content">
          <ModalHeader toggle={() => setMapServiceModal(false)}>
            Map Projects to Application Service
          </ModalHeader>
          <div className="modal-body">
            <Row>
              <Col lg="7">
                <h5 className="text-primary">Selected Projects</h5>
                <Row>
                  {projects.filter(project => checkBox[project.project_id]).map(project => (
                    <Col md="6" key={project.project_id}>
                      <CardBody className="border bg-transparent text-white px-2 my-1">
                        <div className="resource-logo-display mt-1">
                          <h6 className="pt-2 resource-name-truncate">
                            {project.project_name}
                          </h6>
                        </div>
                      </CardBody>
                      <span className="close me-2" onClick={() => handleCheckBox(project.project_id)}></span>
                    </Col>
                    ))}
                </Row>
              </Col>
              <Col lg="5">
                <h5 className="text-primary">Select Application Service</h5>
                <Select
                  isClearable
                  options={[...serviceOptions, { value: "/add-service", label: "Add new service" }]}
                  placeholder="Select an Application Service"
                  className="mt-3"
                  onChange={(selectedOption) => {
                    if (selectedOption && selectedOption.value === "/add-service") {
                      window.location.href = "/add-service";
                      return;
                    }
                    handleServiceFormChange(selectedOption);
                  }}
                  formatOptionLabel={(option) => (
                      <div className="d-flex align-items-center">
                        {option.value === "/add-service" && (
                            <i className="mdi mdi-plus me-1" style={{ fontSize: "16px" }}></i>
                        )}
                        {option.label}
                      </div>
                  )}
                  styles={customStyles}
                  value={selectedService}
                  getOptionLabel={(option) => customOptionRenderer(option)}
                />
                <Select
                  isClearable
                  options={environments}
                  placeholder="Select an Environment"
                  className="mt-3"
                  onChange={setSelectedEnvironment}
                  styles={customStyles}
                  value={selectedEnvironment}
                />
                <Row className="mt-4 justify-content-end">
                  <Col lg="2">
                    <Button color="secondary" onClick={() => setMapServiceModal(false)}>
                      Cancel
                    </Button>
                  </Col>
                  <Col lg="2">
                    <Button color="primary" onClick={handleServiceMapping}>
                      Map
                    </Button>
                  </Col>
                </Row>
              </Col>
            </Row>
          </div>
        </div>
        </Modal>
      </Container>
    </div>
  );
};

export default SentryProjects;